// Campos del editor de reglas sobre las secciones comunes: descripción, palancas, alcance, cuándo
// se paga (la condición), reglas y vigencia.
import type { ReactNode } from 'react';
import { CommissionBonusModeEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionBonusModeEnum';
import { CommissionCountByEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionCountByEnum';
import { CommissionMetricEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionMetricEnum';
import { CommissionOperatorEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionOperatorEnum';
import { CommissionPayTimingEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPayTimingEnum';
import { cn } from '@/lib/cn';
import { ValidityCalendars } from '@/shared/patterns/ValidityCalendars';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import { addDays, localToday } from '../../lib/payoutPeriods';
import {
    conditionDefaults,
    advanceAllowed,
    isRealDate,
    multiplierApplies,
    onSaleAllowed,
    positionHelpOf,
    type RuleFormErrors,
    type RuleFormValues,
} from '../../lib/ruleForm';
import { ADVANCE_REST, describeScope, PAY_TIMING_LABEL } from '../../lib/ruleText';
import { AmountInput, ChoiceChips, EditorField, FieldHelp } from '@/shared/patterns/EditorForm';
import { LIFECYCLE_FIELD_HELP, LifecycleEditorLayout } from '@/shared/patterns/Lifecycle';
import { RULE_HELP } from '../../constants/helpText';
import { FIELD } from '../fieldStyles';
import { RuleScopePicker } from './RuleScopePicker';

const { FIXED, PERCENT } = CommissionBonusModeEnum;
const { PIECE, SALE } = CommissionCountByEnum;
const { POSICION_VENTA_EN_PERIODO, COLOCACION_PERIODO, NPI_M30_VENDEDOR, SKUS_DISTINTOS_PERIODO } =
    CommissionMetricEnum;
const { BETWEEN, IN } = CommissionOperatorEnum;
const { ON_SALE, ON_CUT, MONTHLY } = CommissionPayTimingEnum;

const PAY_TIMING_HELP: Record<CommissionPayTimingEnum, string> = {
    ON_SALE:
        'Se deposita al registrar la venta. Al cerrar la semana se paga o se descuenta la diferencia.',
    ON_CUT: 'Se deposita al cerrar el corte de la semana.',
    MONTHLY: 'Se guarda y se deposita el día 1 del mes siguiente.',
};

const ON_SALE_BLOCKED =
    'Al momento solo cabe en reglas de bono por venta sin otras palancas: el umbral, el multiplicador y la renta se deciden al cerrar la semana.';

/** Apagar "Permanente" propone dos semanas desde el inicio, como el mock. */
const DEFAULT_WINDOW_DAYS = 14;

/** La posición se nombra por lo que cuenta la regla. */
const metricOptions = (
    countBy: CommissionCountByEnum,
): ReadonlyArray<{ value: CommissionMetricEnum | ''; label: string }> => [
    { value: '', label: 'Sin condición · siempre aplica' },
    {
        value: POSICION_VENTA_EN_PERIODO,
        label: countBy === SALE ? 'Número de ventas' : 'Número de piezas',
    },
    { value: COLOCACION_PERIODO, label: 'Monto $ vendido en el periodo' },
    { value: NPI_M30_VENDEDOR, label: 'Calidad de la cartera (NPI M30)' },
    { value: SKUS_DISTINTOS_PERIODO, label: 'Modelos distintos vendidos' },
];

const COUNT_BY_HELP: Record<CommissionCountByEnum, string> = {
    PIECE: 'Cada pieza en el alcance cuenta y cobra: teléfono + funda son dos.',
    SALE: 'Cada venta cuenta y cobra una vez, aunque lleve varias piezas. El % se calcula sobre las piezas de la venta en el alcance.',
};

const METRIC_HELP: Record<
    Exclude<CommissionMetricEnum, typeof POSICION_VENTA_EN_PERIODO>,
    string
> = {
    COLOCACION_PERIODO: 'Suma el precio de todo lo que el vendedor vendió en el periodo, en pesos.',
    NPI_M30_VENDEDOR:
        'Porcentaje de créditos del vendedor con primer impago a 30 días. El dato viene del crédito; mientras no llegue, la regla no dispara.',
    SKUS_DISTINTOS_PERIODO: 'Cuántos modelos distintos vendió el vendedor en el periodo.',
};

const VALUE_UNIT: Record<
    Exclude<CommissionMetricEnum, typeof POSICION_VENTA_EN_PERIODO>,
    string
> = {
    COLOCACION_PERIODO: '$',
    NPI_M30_VENDEDOR: '%',
    SKUS_DISTINTOS_PERIODO: 'modelos',
};

const OPERATOR_OPTIONS: ReadonlyArray<{ value: CommissionOperatorEnum; label: string }> = [
    { value: CommissionOperatorEnum.GTE, label: '≥' },
    { value: CommissionOperatorEnum.LTE, label: '≤' },
    { value: CommissionOperatorEnum.LT, label: '<' },
    { value: CommissionOperatorEnum.GT, label: '>' },
    { value: BETWEEN, label: 'entre' },
    { value: IN, label: 'en la lista' },
];

/** Un renglón de palanca: etiqueta con su «?», monto con unidad y, si aplica, su modo. */
function LeverRow({
    id,
    label,
    help,
    unit,
    error,
    mode,
    children,
}: {
    id: string;
    label: string;
    help: string;
    unit: string;
    error?: string;
    mode?: ReactNode;
    children: (unit: string) => ReactNode;
}) {
    return (
        <div>
            <div className="grid grid-cols-[150px_1fr] items-center gap-2">
                <span className="flex items-center gap-1">
                    <label htmlFor={id} className="text-[11px] font-medium">
                        {label}
                    </label>
                    <FieldHelp label={label} help={help} />
                </span>
                <div className="flex flex-wrap items-center gap-2">
                    {children(unit)}
                    {mode}
                </div>
            </div>
            {error ? <p className="mt-1 text-[10.5px] text-destructive">{error}</p> : null}
        </div>
    );
}

const BONUS_MODE_OPTIONS = (percentLabel: string) =>
    [
        { value: FIXED, label: '$ fijo' },
        { value: PERCENT, label: percentLabel },
    ] as const;

interface RuleFieldsProps {
    values: RuleFormValues;
    errors: RuleFormErrors;
    onChange: (patch: Partial<RuleFormValues>) => void;
    directory: CommissionDirectory;
    /** Las palancas en una línea, como `describirPalancas` del mock. */
    summary: string;
    /** La sección Reglas: lo que el editor sabe de la política (clawback). */
    rules?: ReactNode;
}

export function RuleFields({
    values,
    errors,
    onChange,
    directory,
    summary,
    rules,
}: RuleFieldsProps) {
    const salePercent = values.saleBonusMode === PERCENT;
    const couponPercent = values.couponBonusMode === PERCENT;
    const hasMultiplier = multiplierApplies(values.multiplier);
    const bySale = values.countBy === SALE;
    const metricHelp =
        values.metric === POSICION_VENTA_EN_PERIODO
            ? positionHelpOf(values)
            : values.metric === ''
              ? null
              : METRIC_HELP[values.metric];
    const valueUnit =
        values.metric === POSICION_VENTA_EN_PERIODO
            ? bySale
                ? 'ventas'
                : 'piezas'
            : values.metric === ''
              ? ''
              : VALUE_UNIT[values.metric];
    const integerMetric =
        values.metric === POSICION_VENTA_EN_PERIODO || values.metric === SKUS_DISTINTOS_PERIODO;
    const onSale = onSaleAllowed(values);
    const advance = advanceAllowed(values);
    const today = localToday();

    const description = (
        <div className="flex flex-col gap-2">
            <EditorField label="Nombre" help={RULE_HELP.name} error={errors.name}>
                {(id) => (
                    <input
                        id={id}
                        className={FIELD}
                        placeholder="ej. Boost gama alta"
                        value={values.name}
                        aria-invalid={errors.name ? true : undefined}
                        onChange={(event) => {
                            onChange({ name: event.target.value });
                        }}
                    />
                )}
            </EditorField>
            <EditorField label="Descripción" help={LIFECYCLE_FIELD_HELP.description}>
                {(id) => (
                    <textarea
                        id={id}
                        rows={3}
                        className={cn(FIELD, 'h-auto py-1')}
                        placeholder="Cómo la va a leer el vendedor"
                        value={values.description}
                        onChange={(event) => {
                            onChange({ description: event.target.value });
                        }}
                    />
                )}
            </EditorField>
        </div>
    );

    const levers = (
        <div className="flex flex-col gap-2 rounded-md border border-border p-3">
            <EditorField
                label="Contar por"
                help={RULE_HELP.countBy}
                note={COUNT_BY_HELP[values.countBy]}
            >
                <ChoiceChips
                    options={[
                        { value: PIECE, label: 'Pieza' },
                        { value: SALE, label: 'Venta (ticket)' },
                    ]}
                    value={values.countBy}
                    onPick={(countBy) => {
                        onChange({ countBy });
                    }}
                />
            </EditorField>

            <LeverRow
                id="rule-sale-bonus"
                label="Bono por venta"
                help={RULE_HELP.saleBonus}
                unit={salePercent ? '% del precio' : bySale ? '$ por venta' : '$ por pieza'}
                error={errors.saleBonus}
                mode={
                    <ChoiceChips
                        label="Tipo de bono por venta"
                        options={BONUS_MODE_OPTIONS('% precio')}
                        value={values.saleBonusMode}
                        onPick={(saleBonusMode) => {
                            onChange({ saleBonusMode });
                        }}
                    />
                }
            >
                {(unit) => (
                    <AmountInput
                        id="rule-sale-bonus"
                        type="number"
                        min={0}
                        step={salePercent ? 0.1 : 10}
                        placeholder={salePercent ? 'ej. 2' : 'ej. 80'}
                        unit={unit}
                        value={values.saleBonus}
                        onChange={(event) => {
                            onChange({ saleBonus: event.target.value });
                        }}
                    />
                )}
            </LeverRow>

            <LeverRow
                id="rule-threshold"
                label="Bono al cumplir umbral"
                help={RULE_HELP.thresholdBonus}
                unit="$ una vez por corte"
                error={errors.thresholdBonus}
            >
                {(unit) => (
                    <AmountInput
                        id="rule-threshold"
                        type="number"
                        min={0}
                        step={50}
                        placeholder="ej. 500"
                        unit={unit}
                        value={values.thresholdBonus}
                        onChange={(event) => {
                            onChange({ thresholdBonus: event.target.value });
                        }}
                    />
                )}
            </LeverRow>

            <LeverRow
                id="rule-multiplier"
                label="Multiplicador al corte"
                help={RULE_HELP.multiplier}
                unit="× al corte"
                error={errors.multiplier}
                mode={
                    <label
                        className={cn(
                            'flex items-center gap-1 text-[10.5px]',
                            !hasMultiplier && 'opacity-50',
                        )}
                        title={
                            hasMultiplier
                                ? 'Si dispara, es el único multiplicador del corte del vendedor'
                                : 'Solo con multiplicador'
                        }
                    >
                        <input
                            type="checkbox"
                            checked={values.exclusive && hasMultiplier}
                            disabled={!hasMultiplier}
                            onChange={(event) => {
                                onChange({ exclusive: event.target.checked });
                            }}
                        />
                        exclusiva
                    </label>
                }
            >
                {(unit) => (
                    <AmountInput
                        id="rule-multiplier"
                        type="number"
                        min={0}
                        step={0.05}
                        placeholder="ej. 1.5"
                        unit={unit}
                        value={values.multiplier}
                        onChange={(event) => {
                            const multiplier = event.target.value;
                            // Sin un multiplicador que cambie algo, "exclusiva" se apaga.
                            onChange(
                                multiplierApplies(multiplier)
                                    ? { multiplier }
                                    : { multiplier, exclusive: false },
                            );
                        }}
                    />
                )}
            </LeverRow>

            <LeverRow
                id="rule-coupon"
                label="Bono por cupón pagado"
                help={RULE_HELP.couponBonus}
                unit={couponPercent ? '% del cupón' : '$ por cupón'}
                error={errors.couponBonus}
                mode={
                    <ChoiceChips
                        label="Tipo de bono por cupón"
                        options={BONUS_MODE_OPTIONS('% cupón')}
                        value={values.couponBonusMode}
                        onPick={(couponBonusMode) => {
                            onChange({ couponBonusMode });
                        }}
                    />
                }
            >
                {(unit) => (
                    <AmountInput
                        id="rule-coupon"
                        type="number"
                        min={0}
                        max={couponPercent ? 100 : undefined}
                        step={couponPercent ? 0.1 : 5}
                        placeholder={couponPercent ? 'ej. 1' : 'ej. 5'}
                        unit={unit}
                        value={values.couponBonus}
                        onChange={(event) => {
                            onChange({ couponBonus: event.target.value });
                        }}
                    />
                )}
            </LeverRow>

            <p className="text-[11px] font-medium">{summary}</p>
            <p className="text-[10.5px] text-muted-foreground">
                Llena una o más palancas; el tipo sale de las que llenes. El bono por cupón se paga
                cuando el crédito mande los cupones pagados.
            </p>
            {errors.levers ? (
                <p className="text-[10.5px] text-destructive">{errors.levers}</p>
            ) : null}
            <div className="border-t border-border pt-2">
                <EditorField
                    label="Momento del pago"
                    help={RULE_HELP.payTiming}
                    error={errors.payTiming}
                    note={
                        <>
                            {PAY_TIMING_HELP[values.payTiming]}
                            {!onSale && <span className="block">{ON_SALE_BLOCKED}</span>}
                        </>
                    }
                >
                    <ChoiceChips
                        options={[
                            {
                                value: ON_SALE,
                                label: PAY_TIMING_LABEL.ON_SALE,
                                disabled: !onSale,
                            },
                            { value: ON_CUT, label: PAY_TIMING_LABEL.ON_CUT },
                            { value: MONTHLY, label: PAY_TIMING_LABEL.MONTHLY },
                        ]}
                        value={values.payTiming}
                        onPick={(payTiming) => {
                            onChange({ payTiming });
                        }}
                    />
                </EditorField>
                {advance && (
                    <EditorField
                        label="Adelanto al momento"
                        help={RULE_HELP.advance}
                        error={errors.advancePct}
                        className="mt-2"
                        note={
                            <>
                                {`Se paga al registrar la venta. El resto, con el multiplicador si aplica, se paga ${ADVANCE_REST[values.payTiming]}.`}
                                <span className="block">
                                    Si la venta se cancela antes del cierre, se descuenta lo
                                    adelantado.
                                </span>
                            </>
                        }
                    >
                        {(id) => (
                            <AmountInput
                                inputMode="numeric"
                                id={id}
                                type="number"
                                min={1}
                                max={99}
                                step={1}
                                placeholder="ej. 30"
                                unit="%"
                                value={values.advancePct}
                                aria-invalid={errors.advancePct ? true : undefined}
                                onChange={(event) => {
                                    onChange({ advancePct: event.target.value });
                                }}
                            />
                        )}
                    </EditorField>
                )}
            </div>
        </div>
    );

    const whenPaid = (
        <div className="flex flex-col gap-2">
            <EditorField label="Métrica" help={RULE_HELP.metric} note={metricHelp}>
                {(id) => (
                    <select
                        id={id}
                        className={FIELD}
                        value={values.metric}
                        onChange={(event) => {
                            onChange(
                                conditionDefaults(event.target.value as CommissionMetricEnum | ''),
                            );
                        }}
                    >
                        {metricOptions(values.countBy).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )}
            </EditorField>
            {values.metric !== '' && (
                <div className="grid grid-cols-[110px_1fr] gap-2">
                    <EditorField label="Operador" help={RULE_HELP.operator}>
                        {(id) => (
                            <select
                                id={id}
                                className={FIELD}
                                value={values.operator}
                                onChange={(event) => {
                                    onChange({
                                        operator: event.target.value as CommissionOperatorEnum,
                                    });
                                }}
                            >
                                {OPERATOR_OPTIONS.filter(
                                    (option) => option.value !== IN || values.operator === IN,
                                ).map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </EditorField>
                    <EditorField
                        label={`Valor (${valueUnit})`}
                        help={RULE_HELP.value}
                        error={errors.conditionValue ?? errors.conditionValueTo}
                    >
                        {(id) => (
                            <div className="flex items-center gap-1">
                                <input
                                    id={id}
                                    type={values.operator === IN ? 'text' : 'number'}
                                    min={0}
                                    step={integerMetric ? 1 : 0.01}
                                    className={FIELD}
                                    value={values.conditionValue}
                                    onChange={(event) => {
                                        onChange({ conditionValue: event.target.value });
                                    }}
                                />
                                {values.operator === BETWEEN && (
                                    <>
                                        <span className="text-[11px]">y</span>
                                        <input
                                            aria-label="Hasta"
                                            type="number"
                                            min={0}
                                            step={integerMetric ? 1 : 0.01}
                                            className={FIELD}
                                            value={values.conditionValueTo}
                                            onChange={(event) => {
                                                onChange({ conditionValueTo: event.target.value });
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </EditorField>
                </div>
            )}
            {values.metric === NPI_M30_VENDEDOR && (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-[10.5px]">
                    <EditorField
                        label="Piso de muestra"
                        help={RULE_HELP.minSample}
                        error={errors.minSample}
                        note="Vendedores con menos créditos M30 no evalúan esta regla: evita premiar cartera vacía o castigar por un solo impago."
                    >
                        {(id) => (
                            <AmountInput
                                id={id}
                                type="number"
                                min={1}
                                step={1}
                                unit="créditos"
                                value={values.minSample}
                                onChange={(event) => {
                                    onChange({ minSample: event.target.value });
                                }}
                            />
                        )}
                    </EditorField>
                </div>
            )}
        </div>
    );

    const validity = (
        <>
            <ValidityCalendars
                validFrom={values.effectiveFrom === '' ? null : values.effectiveFrom}
                validTo={values.permanent || values.effectiveTo === '' ? null : values.effectiveTo}
                today={today}
                onChange={({ validFrom, validTo }) => {
                    onChange({
                        effectiveFrom: validFrom ?? '',
                        ...(validTo ? { effectiveTo: validTo, permanent: false } : {}),
                    });
                }}
                onTogglePermanent={(isPermanent) => {
                    const from = isRealDate(values.effectiveFrom) ? values.effectiveFrom : today;
                    onChange(
                        isPermanent
                            ? { permanent: false, effectiveTo: addDays(from, DEFAULT_WINDOW_DAYS) }
                            : { permanent: true },
                    );
                }}
            />
            {(errors.effectiveFrom ?? errors.effectiveTo) ? (
                <p className="mt-1 text-[10.5px] text-destructive">
                    {errors.effectiveFrom ?? errors.effectiveTo}
                </p>
            ) : null}
        </>
    );

    return (
        <LifecycleEditorLayout
            description={description}
            levers={levers}
            scope={<RuleScopePicker values={values} onChange={onChange} directory={directory} />}
            summaries={{ scope: describeScope(values) }}
            whenPaid={whenPaid}
            rules={rules}
            validity={validity}
        />
    );
}
